import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Trees, ArrowRight, Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LanguageSwitcher } from "@/components/layout/LanguageSwitcher";
import { trackHeroCtaClick, trackAppCtaClick } from "@/utils/tracking";

const APP_URL = "https://app.foresthub.app";

export const Navigation = ({ onRequestDemo }) => {
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleNavDemoClick = () => {
    trackHeroCtaClick('nav');
    onRequestDemo();
  };

  const handleNavAppClick = () => {
    trackAppCtaClick('nav');
  };

  const navItems = [
    { href: "#solution", label: t('nav.solution') },
    { href: "#target-groups", label: t('nav.targetGroups') },
    { href: "#use-cases", label: t('nav.useCases') },
    { href: "#process", label: t('nav.process') },
    { href: "#faq", label: t('nav.faq') },
  ];

  return (
    <nav 
      className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-stone-100"
      data-testid="navigation"
    >
      <div className="max-w-6xl mx-auto px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <a 
            href="/" 
            className="flex items-center gap-2.5"
            data-testid="nav-logo"
          >
            <div className="w-9 h-9 rounded-lg bg-[#1B4D3E] flex items-center justify-center">
              <Trees className="w-5 h-5 text-white" />
            </div>
            <span 
              className="text-lg font-semibold text-stone-900" 
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              ForestHub
            </span>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navItems.map((item) => (
              <a
                key={item.href}
                href={item.href}
                className="text-sm text-stone-600 hover:text-[#1B4D3E] transition-colors"
              >
                {item.label}
              </a>
            ))}
          </div>

          {/* Desktop CTAs */}
          <div className="hidden lg:flex items-center gap-3">
            <LanguageSwitcher />
            <Button
              onClick={handleNavDemoClick}
              variant="outline"
              className="border-[#1B4D3E] text-[#1B4D3E] hover:bg-[#1B4D3E]/5 text-sm h-9 px-4 rounded-lg"
              data-testid="nav-demo-btn"
            >
              {t('nav.requestDemo')}
            </Button>
            <a href={APP_URL} target="_blank" rel="noopener noreferrer" onClick={handleNavAppClick}>
              <Button 
                className="bg-[#1B4D3E] hover:bg-[#163D32] text-white text-sm h-9 px-4 rounded-lg"
                data-testid="nav-app-btn"
              >
                {t('nav.toApp')}
                <ArrowRight className="ml-1.5 w-3.5 h-3.5" />
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <div className="flex lg:hidden items-center gap-3">
            <LanguageSwitcher />
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 text-stone-600 hover:text-stone-900"
              data-testid="nav-mobile-menu-btn"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div 
            className="lg:hidden border-t border-stone-100 py-4"
            data-testid="nav-mobile-menu"
          >
            <div className="flex flex-col gap-4">
              {navItems.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  className="text-stone-600 hover:text-[#1B4D3E] transition-colors py-2"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.label}
                </a>
              ))}
              <div className="flex flex-col gap-3 pt-4 border-t border-stone-100">
                <Button
                  onClick={() => {
                    setIsMenuOpen(false);
                    handleNavDemoClick();
                  }}
                  variant="outline"
                  className="border-[#1B4D3E] text-[#1B4D3E] hover:bg-[#1B4D3E]/5 h-10 rounded-lg w-full"
                >
                  {t('nav.requestDemo')}
                </Button>
                <a href={APP_URL} target="_blank" rel="noopener noreferrer" onClick={handleNavAppClick}>
                  <Button className="bg-[#1B4D3E] hover:bg-[#163D32] text-white h-10 rounded-lg w-full">
                    {t('nav.toApp')}
                    <ArrowRight className="ml-1.5 w-3.5 h-3.5" />
                  </Button>
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
